import { enable, disable } from '../base/_tabIndex.js';

export class Modal {
	constructor( triggerSelector, options = {} ) {
		this.triggers = document.querySelectorAll( triggerSelector );

		if ( ! this.triggers.length ) {
			return;
		}

		// Options
		this.modalId = options.modalId || 'agency-finder-modal';
		this.modalSelector = options.modalSelector || `#${ this.modalId }`;
		this.closeSelector = options.closeSelector || '[data-modal-close]';
		this.openClass = options.openClass || 'is-open';
		this.bodyClass = options.bodyClass || 'has-modal-open';

		// Get modal element
		this.modal = document.querySelector( this.modalSelector );

		if ( ! this.modal ) {
			console.warn( `Modal element not found: ${ this.modalSelector }` );
			return;
		}

		// Get close buttons
		this.closeButtons = this.modal.querySelectorAll( this.closeSelector );

		// Get the dialog element (if using <dialog>)
		this.dialog = this.modal.querySelector( 'dialog' );

		// Track if modal is open
		this.isOpen = false;

		// Previous active element for focus management
		this.previousActiveElement = null;

		// First focusable element in modal
		this.firstFocusable = null;

		// Last focusable element in modal
		this.lastFocusable = null;

		this.init();
	}

	init() {
		// Bind methods
		this.open = this.open.bind( this );
		this.close = this.close.bind( this );
		this.handleEscape = this.handleEscape.bind( this );
		this.handleBackdropClick = this.handleBackdropClick.bind( this );
		this.trapFocus = this.trapFocus.bind( this );

		// Add event listeners
		this.triggers.forEach( ( trigger ) => {
			trigger.addEventListener( 'click', ( e ) => {
				e.preventDefault();
				this.open();
			} );
		} );

		// Close buttons
		this.closeButtons.forEach( ( button ) => {
			button.addEventListener( 'click', this.close );
		} );

		// Escape key
		document.addEventListener( 'keydown', this.handleEscape );

		// Backdrop click (if using overlay)
		if ( this.modal.querySelector( '.modal__overlay' ) ) {
			this.modal.querySelector( '.modal__overlay' ).addEventListener( 'click', this.handleBackdropClick );
		}

		// Focus trap
		this.modal.addEventListener( 'keydown', this.trapFocus );
	}

	open() {
		if ( this.isOpen ) {
			return;
		}

		this.isOpen = true;
		this.previousActiveElement = document.activeElement;

		// Show modal
		if ( this.dialog ) {
			// Using HTML5 <dialog> element
			this.dialog.showModal();
		} else {
			// Using custom modal
			this.modal.hidden = false;
			this.modal.classList.add( this.openClass );
			document.body.classList.add( this.bodyClass );
		}

		// Enable tabindex for modal content
		enable( null, this.modal );

		// Find focusable elements
		this.updateFocusableElements();

		// Focus first element
		if ( this.firstFocusable ) {
			this.firstFocusable.focus();
		} else {
			this.modal.focus();
		}

		// Prevent body scroll
		document.body.style.overflow = 'hidden';

		// Dispatch event
		this.modal.dispatchEvent( new CustomEvent( 'modal:open', { bubbles: true } ) );
	}

	close() {
		if ( ! this.isOpen ) {
			return;
		}

		this.isOpen = false;

		// Hide modal
		if ( this.dialog ) {
			this.dialog.close();
		} else {
			this.modal.classList.remove( this.openClass );
			document.body.classList.remove( this.bodyClass );
			// Use setTimeout to allow transition to complete
			setTimeout( () => {
				this.modal.hidden = true;
			}, 300 );
		}

		// Disable tabindex for modal content
		disable( null, this.modal );

		// Restore body scroll
		document.body.style.overflow = '';

		// Restore focus
		if ( this.previousActiveElement ) {
			this.previousActiveElement.focus();
		}

		// Dispatch event
		this.modal.dispatchEvent( new CustomEvent( 'modal:close', { bubbles: true } ) );
	}

	handleEscape( e ) {
		if ( e.key === 'Escape' && this.isOpen ) {
			this.close();
		}
	}

	handleBackdropClick( e ) {
		// Only close if clicking the overlay itself, not its children
		if ( e.target === e.currentTarget ) {
			this.close();
		}
	}

	trapFocus( e ) {
		if ( ! this.isOpen || e.key !== 'Tab' ) {
			return;
		}

		if ( e.shiftKey ) {
			// Shift + Tab
			if ( document.activeElement === this.firstFocusable ) {
				e.preventDefault();
				this.lastFocusable.focus();
			}
		} else {
			// Tab
			if ( document.activeElement === this.lastFocusable ) {
				e.preventDefault();
				this.firstFocusable.focus();
			}
		}
	}

	updateFocusableElements() {
		const focusableSelectors = [
			'a[href]',
			'button:not([disabled])',
			'textarea:not([disabled])',
			'input:not([disabled])',
			'select:not([disabled])',
			'[tabindex]:not([tabindex="-1"])',
		].join( ', ' );

		const focusableElements = Array.from( this.modal.querySelectorAll( focusableSelectors ) ).filter( ( el ) => {
			return el.offsetParent !== null && ! el.hasAttribute( 'hidden' );
		} );

		this.firstFocusable = focusableElements[ 0 ] || null;
		this.lastFocusable = focusableElements[ focusableElements.length - 1 ] || null;
	}
}
